import * as Dialog from "@radix-ui/react-dialog";
import * as Menu from "@radix-ui/react-dropdown-menu";
import { type Asset, ENHANCE_MODELS, enhanceRequestOutputSize, type Job } from "@studio/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AudioLines,
  Camera,
  Clapperboard,
  Download,
  ExternalLink,
  Gem,
  Heart,
  ImageUpscale,
  Pause,
  Pencil,
  Play,
  Sparkles,
  Trash2,
  X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import WaveSurfer from "wavesurfer.js";
import {
  addToImage,
  addToVideo,
  canRedoAtFullQuality,
  download,
  downloadOriginal,
  editJob,
  enhanceAsset,
  moreLikeThis,
  redoAtFullQuality,
  saveFrame,
} from "@/lib/actions";
import { api } from "@/lib/api";
import { useLive } from "@/lib/events";
import { cn, formatClock, formatDuration } from "@/lib/format";
import { token, useTheme } from "@/lib/theme";
import { jobTitle } from "./GpuStatus";
import { documentLength } from "./MediaThumb";
import { FastLabel } from "./RunCard";
import { Button, IconButton } from "./ui";

/** Full-screen on a phone, a large panel on desktop: the result itself, what made it, and what to do next. */
export function AssetViewer({ assetId, onClose }: { assetId: string | null; onClose: () => void }) {
  const asset = useQuery({
    queryKey: ["asset", assetId],
    queryFn: () => api.asset(assetId as string),
    enabled: !!assetId,
  });
  const job = useQuery({
    queryKey: ["job", asset.data?.jobId],
    queryFn: () => api.job(asset.data?.jobId as string),
    enabled: !!asset.data?.jobId,
  });
  const videoRef = useRef<HTMLVideoElement>(null);
  // An HDR result is kept as the asset's original, beside the standard copy every browser plays. Where the
  // screen is an HDR one and the browser decodes the file, the original is what plays; if it then fails to, the
  // copy takes over.
  const hdr = isHdrResult(asset.data ?? null, job.data ?? null);
  const [hdrFailed, setHdrFailed] = useState<string | null>(null);
  const playsHdr = hdr && hdrFailed !== assetId && showsHdrVideo();

  return (
    <Dialog.Root open={!!assetId} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/80" />
        <Dialog.Content
          aria-describedby={undefined}
          className="fixed inset-0 z-50 flex flex-col bg-desk outline-none md:inset-6 md:flex-row md:overflow-hidden md:rounded-[var(--radius-dock)] md:border md:border-line"
        >
          <Dialog.Title className="sr-only">
            {asset.data?.prompt ?? asset.data?.name ?? "Result"}
          </Dialog.Title>
          <div className="pt-safe flex shrink-0 items-center justify-between px-2 md:absolute md:right-2 md:top-2 md:z-10 md:pt-0">
            <Dialog.Close asChild>
              <IconButton label="Close" className="bg-black/40 text-paper md:bg-panel">
                <X className="size-5" />
              </IconButton>
            </Dialog.Close>
          </div>
          <div
            className={cn(
              "relative flex min-h-0 flex-1 items-center justify-center",
              asset.data?.kind === "audio" ? "bg-desk" : "bg-black md:bg-[#141618]",
            )}
          >
            {asset.data ? (
              <Media
                asset={asset.data}
                videoRef={videoRef}
                original={playsHdr}
                onOriginalFailed={() => setHdrFailed(assetId)}
              />
            ) : (
              <div className="size-10 animate-pulse rounded-full bg-panel" />
            )}
          </div>
          {asset.data ? (
            <Details
              asset={asset.data}
              job={job.data ?? null}
              videoRef={videoRef}
              onDeleted={onClose}
              hdr={hdr ? (playsHdr ? "shown" : "copy") : null}
            />
          ) : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** Whether a video is the HDR result of a conversion, with the HDR file kept beside the copy that is shown. */
function isHdrResult(asset: Asset | null, job: Job | null): boolean {
  return (
    asset?.kind === "video" &&
    !!asset.originalUrl &&
    job?.request.type === "enhance" &&
    !!ENHANCE_MODELS[job.request.model]?.hdr
  );
}

/** Whether this browser shows HDR video on this screen: an HDR display, and a decoder for 10-bit HEVC in MP4. */
function showsHdrVideo(): boolean {
  if (typeof window === "undefined" || !window.matchMedia("(dynamic-range: high)").matches) return false;
  return document.createElement("video").canPlayType('video/mp4; codecs="hvc1.2.4.L153.B0"') !== "";
}

function Media({
  asset,
  videoRef,
  original = false,
  onOriginalFailed,
}: {
  asset: Asset;
  videoRef: React.RefObject<HTMLVideoElement | null>;
  /** Play the asset's original (an HDR result) and not the copy made for every browser. */
  original?: boolean;
  onOriginalFailed?: () => void;
}) {
  if (asset.kind === "image") {
    return <img src={asset.url} alt={asset.prompt ?? ""} className="max-h-full max-w-full object-contain" />;
  }
  if (asset.kind === "video") {
    const src = original && asset.originalUrl ? asset.originalUrl : asset.url;
    return (
      // biome-ignore lint/a11y/useMediaCaption: generated videos do not have a caption track
      <video
        ref={videoRef}
        key={src}
        src={src}
        onError={src === asset.url ? undefined : onOriginalFailed}
        poster={asset.thumbUrl ?? undefined}
        controls
        playsInline
        preload="metadata"
        className="max-h-full max-w-full"
      />
    );
  }
  if (asset.kind === "document") return <DocumentPreview asset={asset} />;
  return <AudioPlayer asset={asset} />;
}

/** Desktop browsers show the PDF itself; phones get its first page and open the file in their own viewer. */
function DocumentPreview({ asset }: { asset: Asset }) {
  const [wide] = useState(() => window.matchMedia("(min-width: 768px)").matches);
  if (wide)
    return (
      <iframe
        key={asset.id}
        src={asset.url}
        title={asset.name ?? "Document"}
        className="size-full border-0 bg-white"
      />
    );
  return (
    <a
      href={asset.url}
      target="_blank"
      rel="noreferrer"
      className="relative flex max-h-full max-w-full items-center justify-center p-4"
    >
      {asset.thumbUrl ? (
        <img
          src={asset.thumbUrl}
          alt={asset.name ?? "First page"}
          className="max-h-full max-w-full bg-white object-contain"
        />
      ) : null}
      <span className="absolute bottom-8 inline-flex items-center gap-2 rounded-full bg-film px-4 py-2 text-sm font-medium text-film-ink">
        <ExternalLink className="size-4" aria-hidden />
        Open the PDF
      </span>
    </a>
  );
}

function AudioPlayer({ asset }: { asset: Asset }) {
  const container = useRef<HTMLDivElement>(null);
  const surfer = useRef<WaveSurfer | null>(null);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const peaks = useQuery({ queryKey: ["peaks", asset.id], queryFn: () => api.peaks(asset.id), retry: false });
  const theme = useTheme((s) => s.resolved);

  // Recreate WaveSurfer when the resolved theme changes so its canvas colors track CSS tokens.
  // biome-ignore lint/correctness/useExhaustiveDependencies: theme intentionally triggers recreation
  useEffect(() => {
    if (!container.current || peaks.isLoading) return;
    const ws = WaveSurfer.create({
      container: container.current,
      url: asset.url,
      peaks: peaks.data ? [peaks.data] : undefined,
      duration: asset.durationSec ?? undefined,
      height: 120,
      waveColor: token("--color-line"),
      progressColor: token("--color-signal"),
      cursorColor: token("--color-paper"),
      barWidth: 3,
      barGap: 2,
      barRadius: 3,
      backend: "MediaElement",
    });
    ws.on("play", () => setPlaying(true));
    ws.on("pause", () => setPlaying(false));
    ws.on("timeupdate", (t) => setTime(t));
    surfer.current = ws;
    return () => ws.destroy();
  }, [asset.url, asset.durationSec, peaks.data, peaks.isLoading, theme]);

  return (
    <div className="flex w-full max-w-[760px] flex-col items-center gap-6 px-6">
      <div ref={container} className="w-full" />
      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={() => surfer.current?.playPause()}
          className="inline-flex size-16 items-center justify-center rounded-full bg-film text-signal-ink hover:bg-[#f7cf4f]"
          aria-label={playing ? "Pause" : "Play"}
        >
          {playing ? <Pause className="size-7" /> : <Play className="ml-1 size-7" />}
        </button>
        <span className="tabular text-sm text-mute">
          {formatDuration(time)} / {formatDuration(asset.durationSec)}
        </span>
      </div>
    </div>
  );
}

function Details({
  asset,
  job,
  videoRef,
  onDeleted,
  hdr,
}: {
  asset: Asset;
  job: Job | null;
  videoRef: React.RefObject<HTMLVideoElement | null>;
  onDeleted: () => void;
  /** An HDR result: whether it plays here as HDR ("shown") or as its standard copy ("copy"); null for the rest. */
  hdr: "shown" | "copy" | null;
}) {
  const queryClient = useQueryClient();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const jobs = useLive((s) => s.jobs);

  // An enhanced video above 1920×1080 is shown as a copy within that size; the job says how large it really is.
  const enhanced = job?.request.type === "enhance" ? job.request : null;
  const outSize =
    asset.kind === "video" && enhanced?.sourceWidth && enhanced.sourceHeight
      ? enhanceRequestOutputSize(enhanced)
      : null;
  // only worth a word when the file is larger than what plays
  const fullSize =
    outSize && asset.width && asset.height && outSize.width * outSize.height > asset.width * asset.height
      ? outSize
      : hdr
        ? null
        : outSize;

  const toggleFavorite = async () => {
    const updated = await api.setFavorite(asset.id, !asset.favorite);
    queryClient.setQueryData(["asset", asset.id], updated);
    queryClient.invalidateQueries({ queryKey: ["assets"] });
  };

  const remove = async () => {
    try {
      await api.deleteAsset(asset.id);
      queryClient.invalidateQueries({ queryKey: ["assets"] });
      toast.success("Deleted");
      onDeleted();
    } catch (error) {
      toast.error((error as Error).message);
    }
  };

  return (
    <aside className="pb-safe scrollbar-thin flex max-h-[46dvh] shrink-0 flex-col gap-4 overflow-y-auto border-t border-line bg-panel px-4 pt-4 md:max-h-none md:w-[360px] md:border-l md:border-t-0 md:px-5 md:pb-5 md:pt-14">
      <div>
        <p className="break-words text-[15px] leading-6">
          {asset.prompt ?? asset.name ?? (asset.source === "upload" ? "Uploaded file" : "Saved frame")}
        </p>
        <p className="tabular mt-1 text-[13px] text-mute">
          {job ? `${jobTitle(job)} · ` : ""}
          {asset.kind === "image"
            ? `${asset.width}×${asset.height}`
            : asset.kind === "document"
              ? `PDF, ${documentLength(asset)}`
              : formatDuration(asset.durationSec)}
          {asset.kind === "video" && asset.hasAudio ? " · with sound" : ""} · {formatClock(asset.createdAt)}
          {job?.ranSpeed === "fast" ? (
            <>
              {" "}
              <FastLabel job={job} />
            </>
          ) : null}
        </p>
        {asset.revisedPrompt ? (
          <details className="mt-2 text-[13px] text-mute">
            <summary className="cursor-pointer select-none hover:text-paper">
              What the model was given
            </summary>
            <p className="mt-1 whitespace-pre-wrap break-words leading-5">{asset.revisedPrompt}</p>
          </details>
        ) : null}
      </div>

      <div className="grid grid-cols-2 gap-2">
        {asset.kind === "image" ? (
          <>
            <Button variant="primary" onClick={() => addToImage(asset)}>
              <Pencil className="size-4" aria-hidden />
              Edit image
            </Button>
            <Button variant="outline" onClick={() => addToVideo(asset, "first")}>
              <Clapperboard className="size-4" aria-hidden />
              Animate
            </Button>
            <UseAsMenu asset={asset} />
            <Button variant="outline" onClick={() => enhanceAsset(asset)} title="Restore and enlarge it">
              <ImageUpscale className="size-4" aria-hidden />
              Enhance
            </Button>
          </>
        ) : null}
        {asset.kind === "video" ? (
          <>
            <Button
              variant="outline"
              onClick={() => {
                const t = videoRef.current?.currentTime ?? 0;
                void saveFrame(asset, t, queryClient);
              }}
            >
              <Camera className="size-4" aria-hidden />
              Save this frame
            </Button>
            <Button variant="outline" onClick={() => addToVideo(asset, "reference")}>
              <Clapperboard className="size-4" aria-hidden />
              Use as reference
            </Button>
            {/* an HDR result is not restored further: the models read standard range */}
            {hdr ? null : (
              <Button
                variant="outline"
                className="col-span-2"
                onClick={() => enhanceAsset(asset)}
                title="Restore and enlarge it"
              >
                <ImageUpscale className="size-4" aria-hidden />
                Enhance
              </Button>
            )}
          </>
        ) : null}
        {asset.kind === "document" ? (
          <Button
            variant="outline"
            className="col-span-2"
            onClick={() => window.open(asset.url, "_blank", "noopener")}
          >
            <ExternalLink className="size-4" aria-hidden />
            Open in a new tab
          </Button>
        ) : null}
        {asset.kind === "audio" ? (
          <Button variant="outline" className="col-span-2" onClick={() => addToVideo(asset, "reference")}>
            <AudioLines className="size-4" aria-hidden />
            Use the sound in a video
          </Button>
        ) : null}
        {job && job.type === "enhance" ? (
          <Button variant="outline" className="col-span-2" onClick={() => editJob(job, queryClient)}>
            <Pencil className="size-4" aria-hidden />
            {enhanced && ENHANCE_MODELS[enhanced.model].scales.length > 1
              ? "Enhance again at another size"
              : "Enhance again"}
          </Button>
        ) : null}
        {job && job.type !== "enhance" ? (
          <>
            <Button variant="outline" onClick={() => moreLikeThis(job)}>
              <Sparkles className="size-4" aria-hidden />
              More like this
            </Button>
            <Button variant="outline" onClick={() => editJob(job, queryClient)}>
              <Pencil className="size-4" aria-hidden />
              Edit prompt
            </Button>
            {canRedoAtFullQuality(job, jobs) ? (
              <Button
                variant="outline"
                className="col-span-2"
                title="The same settings and seed, every step"
                onClick={() => redoAtFullQuality(job)}
              >
                <Gem className="size-4" aria-hidden />
                Redo at full quality
              </Button>
            ) : null}
          </>
        ) : null}
        {asset.originalUrl ? (
          <Button
            variant="outline"
            className="col-span-2"
            title="What the model made, before it was converted for playing in a browser"
            onClick={() => downloadOriginal(asset)}
          >
            <Download className="size-4" aria-hidden />
            {hdr
              ? `Download the HDR video${fullSize ? ` (${fullSize.width}×${fullSize.height})` : ""}`
              : fullSize
                ? `Download at full size (${fullSize.width}×${fullSize.height})`
                : "Download the original"}
          </Button>
        ) : null}
      </div>
      {asset.originalUrl && hdr ? (
        <p className="-mt-2 text-[13px] leading-5 text-mute">
          {hdr === "shown"
            ? "It plays here in HDR."
            : "It plays here as a copy in standard range: this screen or this browser does not show HDR video."}{" "}
          The download is the HDR file itself (10-bit HEVC, BT.2020 PQ)
          {fullSize ? `, the whole ${fullSize.width}×${fullSize.height}` : ""}.
        </p>
      ) : null}
      {asset.originalUrl && fullSize && !hdr ? (
        <p className="-mt-2 text-[13px] leading-5 text-mute">
          It plays here at {asset.width}×{asset.height}, the most a browser plays everywhere; the download is
          the whole {fullSize.width}×{fullSize.height}.
        </p>
      ) : null}

      <div className="flex items-center gap-1 border-t border-line pt-3">
        <IconButton
          label={asset.favorite ? "Remove from favourites" : "Add to favourites"}
          onClick={toggleFavorite}
        >
          <Heart className={cn("size-5", asset.favorite && "fill-film text-signal")} />
        </IconButton>
        <IconButton label="Download" onClick={() => download(asset)}>
          <Download className="size-5" />
        </IconButton>
        <div className="flex-1" />
        {confirmDelete ? (
          <>
            <Button size="sm" variant="quiet" onClick={() => setConfirmDelete(false)}>
              Keep
            </Button>
            <Button size="sm" variant="danger" onClick={remove}>
              Delete for good
            </Button>
          </>
        ) : (
          <IconButton label="Delete" onClick={() => setConfirmDelete(true)}>
            <Trash2 className="size-5" />
          </IconButton>
        )}
      </div>
    </aside>
  );
}

function UseAsMenu({ asset }: { asset: Asset }) {
  return (
    <Menu.Root>
      <Menu.Trigger asChild>
        <Button variant="outline">Use in a video</Button>
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Content
          sideOffset={6}
          align="end"
          collisionPadding={12}
          className="z-[60] min-w-[220px] rounded-[var(--radius-control)] border border-line bg-raised p-1 shadow-2xl shadow-black/50"
        >
          {(
            [
              ["first", "As the start frame"],
              ["last", "As the end frame"],
              ["reference", "As a reference"],
            ] as const
          ).map(([slot, label]) => (
            <Menu.Item
              key={slot}
              onSelect={() => addToVideo(asset, slot)}
              className="flex h-11 cursor-pointer items-center rounded-[9px] px-3 text-sm outline-none data-[highlighted]:bg-line"
            >
              {label}
            </Menu.Item>
          ))}
        </Menu.Content>
      </Menu.Portal>
    </Menu.Root>
  );
}
